// Lib
import { get } from 'lodash/fp';

// Utils
import { isCard, isDocument, isImage } from '../../../../common/elements/utils/elementTypeUtils';
import { getElementId, getImageProp } from '../../../../common/elements/utils/elementPropertyUtils';
import { canGiveFeedbackOnBoard } from '../../../../common/permissions/elementPermissionsUtil';
import { getUserOnboardingStep } from '../../../../common/users/utils/userPropertyUtils';

// Selectors
import { getCurrentBoardId, getCurrentBoardIdFromState } from '../../../reducers/currentBoardId/currentBoardIdSelector';
import { getCurrentUser } from '../../../user/currentUserSelector';
import { getShouldShowSuggestionsPopup } from '../../header/workspaceToolsHeader/notifications/pushPopup/pushSuggestionPopupSelector';
import { getGridSizeName } from '../../../utils/grid/gridSizeSelector';
import { getCurrentBoardChildren } from '../../../element/selectors/currentBoardSelector';
import {
    selectIsDesktopAppMode,
    getMilanoteApplicationModeSelector,
    selectIsMobileAppMode,
} from '../../../platform/platformSelector';

// Services
import {
    arePushNotificationsAlreadyPermitted,
    arePushNotificationsDenied,
    arePushNotificationsSupported,
} from '../../../notifications/push/pushNotificationsManager';
import { getHasPushBeenPermanentlyDismissed } from '../../header/workspaceToolsHeader/notifications/pushPopup/pushSuggestionService';
import { getOnboardingProgressStep } from './onboardingProgressService';

// Constants
import {
    DEFAULT_USER_ONBOARDING,
    INVITED_USER_ONBOARDING,
    MOBILE_INVITED_USER_ONBOARDING,
    MOBILE_LEGACY_USER_ONBOARDING_DEPRECATED,
    MOBILE_USER_ONBOARDING,
    ONBOARDING_EDUCATION_CODES,
} from '../../../../common/users/userEducationConstants';
import { PUBLIC_USER_ID } from '../../../../common/users/userConstants';
import { GRID } from '../../../utils/grid/gridConstants';
import { ELEMENT_CREATION_SOURCES } from '../../../../common/elements/elementConstants';
import { getCurrentOnboardingStep } from '../onboardingSelector';
import { OnboardingListener } from './onboardingConfigTypes';
import { MilanoteApplicationMode } from '../../../../common/platform/platformTypes';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';
import { getIsSheetOpen } from '../../../mobile/structural/sheet/sheetSelectors';

const getCardCreateHeaderTitle = (action: any) => action?.content?.textContent?.blocks?.[0].data?.['header-title'];

/**
 * If a user gets sent to an onboarding mode that's not supported by their current app mode, they'll be stuck with
 * UI elements that are disabled & thus unable to proceed through onboarding.
 *
 * This function detects these scenarios and kicks off the appropriate onboarding for the app mode.
 */
const convertOnboardingForAppMode = (currentOnboardingStep: string, appMode: MilanoteApplicationMode): string => {
    const isDesktopAppMode = appMode === MilanoteApplicationMode.desktop;

    switch (currentOnboardingStep) {
        case DEFAULT_USER_ONBOARDING:
        case MOBILE_USER_ONBOARDING:
        case MOBILE_LEGACY_USER_ONBOARDING_DEPRECATED:
            return isDesktopAppMode ? DEFAULT_USER_ONBOARDING : MOBILE_USER_ONBOARDING;
        case INVITED_USER_ONBOARDING:
        case MOBILE_INVITED_USER_ONBOARDING:
            return isDesktopAppMode ? INVITED_USER_ONBOARDING : MOBILE_INVITED_USER_ONBOARDING;
        default:
            return currentOnboardingStep;
    }
};

const ONBOARDING_LISTENERS: OnboardingListener[] = [
    // This listener will kick off an onboarding flow if the user has their "onboarding" property set.
    {
        id: 'enter-onboarding',
        // If we have a stored progress step on the user, start from there
        // The user should always be available at this point, because the listeners are attached after
        // the CURRENT_USER_SET action.
        attachmentPredicate: (state) => {
            const currentUser = getCurrentUser(state);
            return !!getUserOnboardingStep(currentUser);
        },
        predicate: (action) => action.type === 'ONBOARDING_STEPS_LOADED',
        gotoStep: (state) => {
            const currentUser = getCurrentUser(state);
            const currentUserOnboardingStep = getUserOnboardingStep(currentUser);
            const appMode = getMilanoteApplicationModeSelector(state);
            return convertOnboardingForAppMode(currentUserOnboardingStep, appMode);
        },
    },
    // This listener will kick off an onboarding flow if the browser has a "milanote.onboarding.progress" local
    // storage property set.  This is used to continue a previously unfinished onboarding flow.
    {
        id: 're-enter-onboarding',
        // If we have a stored progress step, start from there
        attachmentPredicate: (state) => {
            const onboardingProgressStep = getOnboardingProgressStep();
            const currentUser = getCurrentUser(state);

            // If the current user has an onboarding step, then don't overwrite it
            return !getUserOnboardingStep(currentUser) && !!onboardingProgressStep;
        },
        predicate: (action) => action.type === 'ONBOARDING_STEPS_LOADED',
        gotoStep: () => getOnboardingProgressStep(),
    },
    {
        id: 'create-note',
        predicate: (action) =>
            !action.remote && action.type === 'ELEMENT_CREATE' && isCard(action) && !getCardCreateHeaderTitle(action),
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-note' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_NOTE,
        preloadImages: [require('../tooltips/explain-note.png')],
    },
    {
        id: 'create-document',
        predicate: (action) => !action.remote && action.type === 'ELEMENT_CREATE' && isDocument(action),
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-document' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_DOCUMENT,
        preloadImages: [require('../tooltips/explain-document.png')],
    },
    {
        id: 'create-audio',
        predicate: (action) =>
            !action.remote &&
            action.type === 'ELEMENT_CREATE' &&
            action.elementType === 'LINK' &&
            action.content?.linkType === 'AUDIO',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-audio' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_AUDIO,
        preloadImages: [require('../tooltips/explain-audio.png')],
    },
    {
        id: 'create-map',
        predicate: (action) =>
            !action.remote &&
            action.type === 'ELEMENT_CREATE' &&
            action.elementType === 'LINK' &&
            action.content?.linkType === 'MAP',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-map' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_MAP,
        preloadImages: [require('../tooltips/explain-map.png')],
    },
    {
        id: 'create-video',
        predicate: (action) =>
            !action.remote &&
            action.type === 'ELEMENT_CREATE' &&
            action.elementType === 'LINK' &&
            action.content?.linkType === 'VIDEO',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-video' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_VIDEO,
        preloadImages: [require('../tooltips/explain-video.png')],
    },
    {
        id: 'create-sketch',
        predicate: (action) => !action.remote && action.type === 'ELEMENT_CREATE' && action.elementType === 'SKETCH',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-sketch' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_SKETCH,
        preloadImages: [require('../tooltips/explain-sketch.png')],
    },
    {
        id: 'create-swatch',
        predicate: (action) =>
            !action.remote && action.type === 'ELEMENT_CREATE' && action.elementType === 'COLOR_SWATCH',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-swatch' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_SWATCH,
        preloadImages: [require('../tooltips/explain-swatch.png')],
    },
    {
        id: 'create-title',
        predicate: (action) =>
            !action.remote &&
            action.type === 'ELEMENT_CREATE' &&
            action.elementType === 'CARD' &&
            !!getCardCreateHeaderTitle(action),
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-title' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_TITLE,
        preloadImages: [require('../tooltips/explain-title.png')],
    },
    {
        id: 'create-task-list',
        predicate: (action) => !action.remote && action.type === 'ELEMENT_CREATE' && action.elementType === 'TASK_LIST',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-task-list' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_TASK_LIST,
        preloadImages: [require('../tooltips/explain-task-list.png')],
    },
    {
        id: 'create-line',
        predicate: (action) => !action.remote && action.type === 'ELEMENT_CREATE' && action.elementType === 'LINE',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-line' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_LINE,
        preloadImages: [require('../tooltips/explain-line.png')],
    },
    {
        id: 'create-link',
        predicate: (action) =>
            !action.remote &&
            action.type === 'ELEMENT_CREATE' &&
            action.elementType === 'LINK' &&
            !action.content?.linkType,
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-link' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_LINK,
        preloadImages: [require('../tooltips/explain-link.png')],
    },
    {
        id: 'create-column',
        predicate: (action) => !action.remote && action.type === 'ELEMENT_CREATE' && action.elementType === 'COLUMN',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-column' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_COLUMN,
        preloadImages: [require('../tooltips/explain-column-bg.png'), require('../tooltips/explain-column-drag.png')],
    },
    {
        id: 'create-board',
        predicate: (action, state) => {
            if (action.remote) return false;
            if (action.type !== 'ELEMENT_CREATE') return false;
            if (action.elementType !== 'BOARD') return false;

            const currentBoardId = getCurrentBoardIdFromState(state);
            return !state.getIn(['elements', currentBoardId, 'location', 'rootBoard']);
        },
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-board' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_BOARD,
        preloadImages: [require('../tooltips/explain-board.png')],
    },
    {
        id: 'used-template-with-image-placeholder',
        predicate: (action, state) => {
            if (action.type !== 'ONBOARDING_STEP_PASSED') return false;

            const imagePlaceholders = getCurrentBoardChildren(state)
                .filter(isImage)
                .filter((imageElement) => !getImageProp(imageElement));

            if (imagePlaceholders.size === 0) return false;

            action.imageElementId = getElementId(imagePlaceholders.first());

            return true;
        },
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-image-placeholder' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_IMAGE_PLACEHOLDER,
    },
    {
        id: 'create-card-double-click',
        predicate: (action) =>
            !action.remote &&
            action.type === 'ELEMENT_CREATE' &&
            action.elementType === 'CARD' &&
            action.creationSource === ELEMENT_CREATION_SOURCES.DOUBLE_CLICK,
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-double-click' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_DOUBLE_CLICK_CARD,
    },
    {
        id: 'create-comment-thread',
        predicate: (action) =>
            !action.remote && action.type === 'ELEMENT_CREATE' && action.elementType === 'COMMENT_THREAD',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-comment-thread' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_COMMENT_THREAD,
        preloadImages: [require('../tooltips/explain-comment-thread.png')],
    },
    {
        id: 'explain-comment',
        predicate: (action, state) => {
            if (action.remote) return false;

            if (action.type !== 'CURRENT_BOARD_ID_SET' && action.type !== '@@router/LOCATION_CHANGE') return false;

            if (
                action.type === '@@router/LOCATION_CHANGE' &&
                get(['locationBeforeTransitions', 'pathname'], state.get('routing')) !== '/share'
            ) {
                return false;
            }

            const currentBoardId =
                action.type === '@@router/LOCATION_CHANGE' ? getCurrentBoardId(state) : action.boardId;

            const currentBoard = state.getIn(['elements', currentBoardId]);

            if (!currentBoard) return false;
            if (currentBoard.getIn(['location', 'rootBoard'])) return false;

            const currentUserId = state.getIn(['app', 'currentUser', '_id']);
            const aclIds = state.getIn(['permissions', 'aclIds']);
            if (!canGiveFeedbackOnBoard(state.get('elements'), currentBoardId, [currentUserId, ...aclIds])) {
                return false;
            }

            const currentBoardAcl = currentBoard.get('acl');
            return (
                currentBoardAcl &&
                (currentBoardAcl.size > 1 || (currentBoardAcl.size && !currentBoardAcl.get(PUBLIC_USER_ID)))
            );
        },
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-comment' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_COMMENT,
    },
    {
        id: 'suggest-push-notifications',
        // This determines if the listener should be attached
        attachmentPredicate: (state) => {
            // Only attach if the user has had the
            const gridSizeName = getGridSizeName(state);

            // Only suggest this if we have no other onboarding steps running,
            // as it will override the current onboarding flow
            const currentOnboardingStep = getCurrentOnboardingStep(state);

            return (
                !currentOnboardingStep &&
                gridSizeName !== GRID.MOBILE.name &&
                arePushNotificationsSupported() &&
                !arePushNotificationsAlreadyPermitted() &&
                !arePushNotificationsDenied() &&
                !getHasPushBeenPermanentlyDismissed()
            );
        },
        // This determines if the step should be executed
        predicate: (action, state) => {
            const shouldShowSuggestPushPopup = getShouldShowSuggestionsPopup(state);
            const onboardingStepsLoaded = state.getIn(['app', 'onboarding', 'loaded']);
            return shouldShowSuggestPushPopup && onboardingStepsLoaded;
        },
        gotoStep: 'open-suggest-push-popup',
    },
    {
        id: 'create-table',
        predicate: (action) => !action.remote && action.type === 'ELEMENT_CREATE' && action.elementType === 'TABLE',
        gotoStep: (state) => (selectIsDesktopAppMode(state) ? 'explain-table' : null),
        educationCode: ONBOARDING_EDUCATION_CODES.EXPLAIN_TABLE,
        preloadImages: [require('../tooltips/explain-table.png')],
    },
    {
        id: 'mobile-upload',
        // The app mode is checked per action rather than at attachment time, because on iOS it follows the
        // window and the listener stays attached for the whole session.
        predicate: (action, state) =>
            action.type === 'ELEMENT_ATTACHMENT_UPLOAD_COMPLETED' &&
            !action.remote &&
            selectIsMobileAppMode(state) &&
            !getIsSheetOpen(SheetId.Inbox)(state),
        gotoStep: 'explain-mobile-upload',
        preventRemoveListener: true,
    },
];

export default ONBOARDING_LISTENERS;
